import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';

export const Route = createFileRoute('/reward')({ component: RewardPage });

interface Category { id: string; name: string; color: string }
interface CatalogReward {
  id: string; name: string; description: string; icon: string; categoryId: string | null;
}
interface UserReward {
  id: string; rewardId: string; source: 'manual' | 'achievement' | 'purchase';
  claimedAt: string; usedAt: string | null;
  name: string; description: string; icon: string; categoryId: string | null;
}

function RewardPage() {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ['rewards'], queryFn: () => api<CatalogReward[]>('/api/rewards') });
  const mine = useQuery({ queryKey: ['user-rewards'], queryFn: () => api<UserReward[]>('/api/user-rewards') });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/api/categories') });

  const [filterCategoryIds, setFilterCategoryIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState<UserReward | null>(null);

  const allCats = cats.data ?? [];

  const { owned, used, catalogFiltered } = useMemo(() => {
    function match(r: { categoryId: string | null }) {
      return filterCategoryIds.size === 0 || (r.categoryId !== null && filterCategoryIds.has(r.categoryId));
    }
    const m = mine.data ?? [];
    const c = catalog.data ?? [];
    return {
      owned: m.filter((u) => !u.usedAt && match(u)),
      used: m.filter((u) => u.usedAt && match(u)),
      catalogFiltered: c.filter(match),
    };
  }, [mine.data, catalog.data, filterCategoryIds]);

  const claim = useMutation({
    mutationFn: (rewardId: string) => api('/api/user-rewards', {
      method: 'POST', body: JSON.stringify({ rewardId }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-rewards'] }),
  });

  const use = useMutation({
    mutationFn: (id: string) => api(`/api/user-rewards/${id}/use`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-rewards'] });
      setDetail(null);
    },
  });

  function toggleCat(id: string) {
    setFilterCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-100">Rewards</h1>
        <button onClick={() => setAddOpen(true)}
                className="rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600">
          + Yeni reward tanımla
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
        <aside className="rounded-xl border border-border-soft bg-bg-raised p-4">
          <h2 className="mb-3 text-sm font-semibold text-neutral-200">Kategoriye göre</h2>
          <div className="space-y-1">
            <button
              onClick={() => setFilterCategoryIds(new Set())}
              className={`w-full rounded-md px-2 py-1 text-left text-sm transition ${
                filterCategoryIds.size === 0 ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-300 hover:bg-bg-elevated'
              }`}
            >
              Tümü
            </button>
            {allCats.map((c) => (
              <button key={c.id} onClick={() => toggleCat(c.id)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm transition ${
                        filterCategoryIds.has(c.id) ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-300 hover:bg-bg-elevated'
                      }`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                {c.name}
              </button>
            ))}
            {allCats.length === 0 && (
              <p className="text-xs text-neutral-500">Ayarlar'dan kategori ekle</p>
            )}
          </div>
        </aside>

        <div className="space-y-6">
          <Section title="Sahip olunan" count={owned.length} empty="Boş — katalogdan al">
            <Grid items={owned} onClick={(r) => setDetail(r)} />
          </Section>
          <Section title="Kullanılmış" count={used.length} dim empty="Henüz kullanılmış reward yok">
            <Grid items={used} dim />
          </Section>
          <Section title="Katalog" count={catalogFiltered.length} empty="Henüz tanımlı reward yok">
            <CatalogGrid items={catalogFiltered} onClaim={(id) => claim.mutate(id)} />
          </Section>
        </div>
      </div>

      {addOpen && <AddRewardModal onClose={() => setAddOpen(false)}
                                  onSaved={() => qc.invalidateQueries({ queryKey: ['rewards'] })}
                                  cats={allCats} />}
      {detail && <UseModal ur={detail}
                           onClose={() => setDetail(null)}
                           onUse={() => use.mutate(detail.id)} />}
    </div>
  );
}

function Section({ title, count, children, empty, dim }:
  { title: string; count: number; children: React.ReactNode; empty: string; dim?: boolean }) {
  return (
    <section>
      <h2 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${dim ? 'text-neutral-500' : 'text-neutral-200'}`}>
        {title}
        <span className="font-pixel text-[10px] text-neutral-500">{count}</span>
      </h2>
      {count === 0 ? <Empty label={empty} /> : children}
    </section>
  );
}

function Grid({ items, onClick, dim }: {
  items: UserReward[]; onClick?: (r: UserReward) => void; dim?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
      {items.map((r) => (
        <button key={r.id} onClick={() => onClick?.(r)} disabled={!onClick}
                className={`flex flex-col items-center gap-2 rounded-xl border border-border-soft bg-bg-raised p-4 transition ${
                  dim ? 'opacity-50' : 'hover:border-brand-400 hover:bg-bg-raised/70 cursor-pointer'
                }`}>
          <span className="text-4xl">{r.icon}</span>
          <span className="truncate text-xs text-neutral-200">{r.name}</span>
        </button>
      ))}
    </div>
  );
}

function CatalogGrid({ items, onClaim }: { items: CatalogReward[]; onClaim: (id: string) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((r) => (
        <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border-soft bg-bg-raised p-3">
          <span className="text-3xl">{r.icon}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-neutral-100">{r.name}</p>
            <p className="line-clamp-2 text-xs text-neutral-400">{r.description || '—'}</p>
          </div>
          <button onClick={() => onClaim(r.id)}
                  className="shrink-0 rounded-md bg-brand-500 px-2 py-1 text-xs font-medium text-white hover:bg-brand-600">
            Al
          </button>
        </div>
      ))}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border-soft bg-bg-raised/40 p-6 text-center text-sm text-neutral-500">
      {label}
    </div>
  );
}

function UseModal({ ur, onClose, onUse }: { ur: UserReward; onClose: () => void; onUse: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-80 rounded-xl border border-border-soft bg-bg-raised p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-center gap-3">
          <div className="text-6xl">{ur.icon}</div>
          <h3 className="text-lg font-semibold text-neutral-100">{ur.name}</h3>
          <p className="text-center text-sm text-neutral-400">{ur.description || '—'}</p>
          <button onClick={onUse}
                  className="mt-2 w-full rounded-md bg-brand-500 py-2 font-medium text-white hover:bg-brand-600">
            Kullan
          </button>
        </div>
      </div>
    </div>
  );
}

function AddRewardModal({ onClose, onSaved, cats }:
  { onClose: () => void; onSaved: () => void; cats: Category[] }) {
  const [form, setForm] = useState({ name: '', description: '', icon: '🎁', categoryId: '' });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = useMutation({
    mutationFn: () => api('/api/rewards', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name,
        description: form.description,
        icon: form.icon,
        categoryId: form.categoryId || undefined,
      }),
    }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'hata'),
  });

  const ICON_OPTIONS = ['🎁','🍕','🍺','🎮','🎬','📚','🎧','☕','🛌','🏖️','💸','🏆','🕹️','🧋'];

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); setErr(null); create.mutate(); }}
            onClick={(e) => e.stopPropagation()}
            className="w-96 space-y-3 rounded-xl border border-border-soft bg-bg-raised p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-neutral-100">Yeni Reward</h3>
        <input placeholder="İsim" value={form.name} required autoFocus
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
        <textarea placeholder="Açıklama" value={form.description} rows={2}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-md bg-bg-elevated px-3 py-2 text-sm text-neutral-100" />
        <div>
          <label className="text-xs text-neutral-400">İkon</label>
          <div className="mt-1 grid grid-cols-7 gap-2">
            {ICON_OPTIONS.map((i) => (
              <button key={i} type="button" onClick={() => setForm({ ...form, icon: i })}
                      className={`flex aspect-square items-center justify-center rounded-md border text-xl transition ${
                        form.icon === i ? 'border-brand-400 bg-bg-elevated' : 'border-border-soft/60 hover:border-brand-500/60'
                      }`}>
                {i}
              </button>
            ))}
          </div>
        </div>
        <select value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100">
          <option value="">(kategori yok)</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {err && <p className="text-sm text-red-400">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-md px-3 py-2 text-sm text-neutral-400 hover:text-neutral-100">
            İptal
          </button>
          <button type="submit" disabled={create.isPending}
                  className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            Ekle
          </button>
        </div>
      </form>
    </div>
  );
}
